import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button, Collapse, DatePicker, Row, Col, Upload, Checkbox } from "antd";
import {
  UploadOutlined,
  PlusCircleOutlined,
  MinusCircleOutlined,
  UpOutlined,
  EyeOutlined,
  DownloadOutlined,
} from "@ant-design/icons";

import VendorOnboardingService from "../../../services/VendorOnboarding/vendorOnboarding-service";
import { NotificationManager } from "react-notifications";
import FormInput from "../../../components/Common/FormInput/FormInput";
import FormSelect from "../../../components/Common/FormSelect/FormSelect";
import { useSelector } from "react-redux";
import moment from "moment";
import { useNavigate, useLocation } from "react-router-dom";
import TextArea from "antd/lib/input/TextArea";
import useDecryptData from "../../../utils/DecryptData";

const { Panel } = Collapse;

const MAX_FILE_MB = 15;
const SUPPORTED_MIME_TYPES = new Set([
  "application/pdf",
  "image/png",
  "image/jpeg",
]);
const ACCEPT_EXTS = ".pdf,.png,.jpg,.jpeg";
const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: "smooth" });
};

const formatDate = (d) => {
  return d ? moment(d).format("YYYY-MM-DD HH:mm:ss") : "";
};
const safeTrim = (v) => (typeof v === "string" ? v.trim() : v ?? "");

const normalizeVendorApiToForm = (api) => {
  const vd = api?.vendorDetails || {};
  const vb = api?.vendorBankDetails || {};
  const vp = api?.vendorPurchasingData || {};
  const vc = api?.vendorAdditionalCompanyData || {};

  return {
    vendorType: safeTrim(vd.vendorType),
    country: safeTrim(vd.countryCode),
    language: safeTrim(vd.languageKey),
    regionCode: safeTrim(vd.regionCode),
    vendorCategory: safeTrim(vd.vendorCategory),

    // names / header
    titleKey: safeTrim(vd.titleKey),
    name1: safeTrim(vd.name1),
    name2: safeTrim(vd.name2),
    name3: safeTrim(vd.name3),
    name4: safeTrim(vd.name4),
    coName: safeTrim(vd.coName),

    // address
    street1: safeTrim(vd.street1),
    street2: safeTrim(vd.street2),
    street3: safeTrim(vd.street3),
    street4: safeTrim(vd.street4),
    location: safeTrim(vd.location),
    district: safeTrim(vd.district),
    postalCity: safeTrim(vd.postalCode),
    cityName: safeTrim(vd.cityName),

    // contact
    transportZone: safeTrim(vd.transportationZone),
    certificateDate: vd.certificateDate ? moment(vd.certificateDate) : null,
    telephone: safeTrim(vd.telephoneNo),
    fax: safeTrim(vd.faxNo),
    mobile1: safeTrim(vd.mobile1),
    mobile2: safeTrim(vd.mobile2),
    mobile3: safeTrim(vd.mobile3),
    email1: safeTrim(vd.email1),
    email2: safeTrim(vd.email2),

    // bank
    bankDetailsId: vb.bankDetailsID ?? "0001",
    bankCountryKey: safeTrim(vb.bankCountryKey),
    bankKey: safeTrim(vb.bankKey),
    bankAccountNo: safeTrim(vb.bankAccountNo),
    bankName: safeTrim(vb.bankName),
    IFSCOrSWIFTCode: safeTrim(vb.ifsC_SWIFT_Code),
    IECNumber: safeTrim(vb.ieC_Number),

    // others / legal
    pan: safeTrim(vb.permanentAccountNo),
    companyRegistrationNo: safeTrim(vb.companyRegistrationNo),
    panRefNo: safeTrim(vb.panReferenceNo),
    panValidDate: vb.panValidFromDate ? moment(vb.panValidFromDate) : null,
    gstNo: safeTrim(vb.gstNo),
    eccNumber: safeTrim(vb.eccNumber),
    supplierMaster: safeTrim(vb.supplierMaster),
    customerVendor: vb.customerVendor,
    cstNo: safeTrim(vb.centralSalesTaxNumber),
    lstNo: safeTrim(vb.localSalesTaxNumber),
    serviceTaxRegNo: safeTrim(vb.serviceTaxRegNo),

    // msme
    msmeNo: safeTrim(vd.msmeNo),
    msmeCategory: safeTrim(vd.msmeCategory),
    msmeCertDate: vd.msmeCertificatedate
      ? moment(vd.msmeCertificatedate)
      : null,

    // companyRegistrationDetails: "",

    vendorId: vd.vendorId,
    purchaseOrg: safeTrim(vp.purchaseOrg),
    orderCurrency: safeTrim(vp.orderCurrency),
    paymentTerm: safeTrim(vp.paymentTerms),
    purchaseGroup: safeTrim(vp.purchaseGroup),
    plannedDeliveryDelay: safeTrim(vp.plannedDelDays),
    schemaGroup: safeTrim(vp.schemaGroup),
    grInvoiceVerification:
      vp.grBasedInvoiceVerification === "False" ? false : true,

    serviceInvoiceVerification:
      vp.serviceBasedInvoiceVerification === "False" ? false : true,

    incoterm: safeTrim(vp.incoterms),
    incotermLocation: safeTrim(vp.incotermsLocation),

    // Vendor Company Data (NEW FIELDS)

    companyCode:
      safeTrim(vc.companyCode) || localStorage.getItem("Company_Code"),
    reconciliationAccount: safeTrim(vc.reconciliationAccount),
    headOfficeAccountNo: safeTrim(vc.hoAccountNo),
    companyPaymentTerm: vc.paymentTerm ? String(vc.paymentTerm) : "",

    paymentMethod: safeTrim(vc.paymentMethod),
    houseBank: safeTrim(vc.houseBank),
    vendorAccountGroup: vc.vendorAccountGroup
      ? String(safeTrim(vc.vendorAccountGroup))
      : "Z001",
    withholdingTaxType: vc.withholdingTaxType1
      ? String(vc.withholdingTaxType1)
      : "",
    withholdingTaxCode: vc.withholdingTaxCode1
      ? String(vc.withholdingTaxCode1)
      : "",
    withholdingSubject: vc.subjectToWithholdingTax1 === true ? true : false,
    doubleInvoiceCheck: vc.checkDoubleInvoice === "False" ? false : true,

    sectionCode: safeTrim(vc.sectionCode),
    exemptionNumber: safeTrim(vc.exemptionNumber),
    exemptionRate: safeTrim(vc.exemptionRate),
    exemptionTo: vc.exemptionTo ? moment(vc.exemptionTo) : null,
    exemptionReason: safeTrim(vc.exemptionReason),
    withholdingTax: safeTrim(vc.withholdingTaxType),
    WithholdingTaxCode: safeTrim(vc.withholdingTaxCode),
    thresholdAmount: safeTrim(vc.thresholdAmount),
    currencyType: safeTrim(vc.currency),
  };
};

const pickAttachmentSlots = (attachments = []) => {
  const keyFromType = (t = "") => {
    switch (String(t).trim().toLowerCase()) {
      case "pancard":
        return "panCard";
      case "gstcertificate":
        return "gstCertificate";
      case "cancelledcheque":
        return "cancelledCheque";
      default:
        return null;
    }
  };

  const latest = { panCard: null, gstCertificate: null, cancelledCheque: null };

  for (const att of attachments) {
    const k = keyFromType(att?.fileType);
    if (!k) continue;
    const ts = Date.parse(att?.uploadedOn) || 0;
    const entry = {
      uid: String(att?.attachmentId ?? Math.random()),
      name: att?.fileName ?? "file",
      url: att?.filePath ?? "",
      status: "done",
      _ts: ts,
    };
    if (!latest[k] || ts > latest[k]._ts) latest[k] = entry;
  }

  const toUploadList = (v) =>
    v ? [{ uid: v.uid, name: v.name, url: v.url }] : [];

  return {
    fileLists: {
      panCard: toUploadList(latest.panCard),
      gstCertificate: toUploadList(latest.gstCertificate),
      cancelledCheque: toUploadList(latest.cancelledCheque),
    },
    paths: {
      panCard: latest.panCard?.url || "",
      gstCertificate: latest.gstCertificate?.url || "",
      cancelledCheque: latest.cancelledCheque?.url || "",
    },
  };
};

const ViewVendorDetail = (props) => {
  const userDetails = useSelector(
    (state) => state.loginReducer.loginDetials.userDetails
  );
  const navigate = useNavigate();
  const location = useLocation();
  const decryptData = useDecryptData();
  const search = new URLSearchParams(location.search);
  const encryptedMode = search.get("mode");
  let mode = "view";
  if (encryptedMode) {
    try {
      mode = decryptData(decodeURIComponent(encryptedMode)) || "view";
    } catch {
      mode = "view";
    }
  }
  const routedMode = props?.routedMode || mode;
  const isView = routedMode === "view";
  const vendorIdFromProps = props?.vendorId;
  const vendorIdFromUrl = search.get("vendorId");
  const vendorId =
    vendorIdFromProps || vendorIdFromUrl || userDetails?.vendorId;
  const [actionLoading, setActionLoading] = useState(null);
  const [countryList, setCountryList] = useState([]);
  const [languageList, setLanguageList] = useState([]);
  const [typeOfVendorList, setTypeOfVendorList] = useState([]);
  const [regionalCodeList, setRegionalCodeList] = useState([]);
  const [vendorCategoryList, setVendorCategoryList] = useState([]);
  const [titleList, setTitleList] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitNow, setSubmitNow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [userRoleName, setUserRoleName] = useState("");
  const [purchaseOrgOptions, setPurchaseOrgOptions] = useState([]);
  const [purchaseGroupOptions, setPurchaseGroupOptions] = useState([]);
  const [orderCurrencyOptions, setOrderCurrencyOptions] = useState([]);
  const [paymentTermOptions, setPaymentTermOptions] = useState([]);
  const [schemaGroupOptions, setSchemaGroupOptions] = useState([]);
  const [paymentMethodOptions, setPaymentMethodOptions] = useState([]);
  const [reconAccountOptions, setReconAccountOptions] = useState([]);
  const [houseBankOptions, setHouseBankOptions] = useState([]);
  const [withholdingTypeOptions, setWithholdingTypeOptions] = useState([]);
  const [withholdingCodeOptions1, setWithholdingCodeOptions1] = useState([]);
  const [withholdingCodeOptions2, setWithholdingCodeOptions2] = useState([]);
  const [initialForm, setInitialForm] = useState(null);
  const [canFullEdit, setCanFullEdit] = useState(false);
  const [isIndiaSelected, setIsIndiaSelected] = useState(false);

  const [files, setFiles] = useState({
    panCard: null,
    gstCertificate: null,
    cancelledCheque: null,
  });
  const [fileLists, setFileLists] = useState({
    panCard: [],
    gstCertificate: [],
    cancelledCheque: [],
  });
  const [existingFilePaths, setExistingFilePaths] = useState({
    panCard: "",
    gstCertificate: "",
    cancelledCheque: "",
  });

  const [vendorRole, setVendorRole] = useState("");

  const role = useMemo(() => vendorRole.trim().toLowerCase(), [vendorRole]);

  const showPurchaseData =
    userDetails?.isEmployee &&
    (role === "internal user" ||
      role === "admin" ||
      role === "finance team" ||
      role === "finance hod");

  const showCompanyData =
    role === "finance team" || role === "admin" || role === "finance hod";
  const showDataInfoPanel = showPurchaseData || showCompanyData;

  const isEditMode = routedMode === "edit";
  const isViewMode = routedMode === "view";

  const canEditPurchaseData =
    (canFullEdit && isEditMode) ||
    (isEditMode && (role === "internal user" || role === "admin"));

  const canEditCompanyData =
    (canFullEdit && isEditMode) ||
    (isEditMode && (role === "finance team" || role === "admin"));
  const canEditCompanyDataValidation =
    // (canFullEdit && isEditMode) ||
    isEditMode &&
    (role === "finance team" || role === "admin" || role === "finance hod");
  const isFinanceHodReadOnly = isEditMode && role === "finance hod";
  const mountedRef = useRef(true);
  const isAdmin = role === "admin";
  const isFinanceHod = role === "finance hod";
  const disableForGeneral =
    isViewMode ||
    (isEditMode &&
      !userDetails.isVendor &&
      !(isAdmin || role === "internal user"));
  const disableForHod = isViewMode || (isEditMode && isFinanceHod);
  const disable = !(canFullEdit && isEditMode)
    ? disableForGeneral || disableForHod
    : false;

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (userDetails?.vendorRole) {
      setVendorRole(String(userDetails.vendorRole).trim().toLowerCase());
    }
  }, [userDetails?.vendorRole]);

  const [form, setForm] = useState({
    vendorType: "",
    country: null,
    language: null,
    regionCode: null,
    vendorCategory: null,

    titleKey: "",
    name1: "",
    name2: "",
    name3: "",
    name4: "",
    coName: "",

    street1: "",
    street2: "",
    street3: "",
    street4: "",
    location: "",
    district: "",
    postalCity: "",
    cityName: "",

    transportZone: "",
    certificateDate: null,
    telephone: "",
    fax: "",
    mobile1: "",
    mobile2: "",
    mobile3: "",
    email1: "",
    email2: "",

    bankDetailsId: "0001",
    bankCountryKey: "",
    bankKey: "",
    bankAccountNo: "",
    bankName: "",
    IFSCOrSWIFTCode: "",
    IECNumber: "",

    pan: "",
    companyRegistrationNo: "",
    panRefNo: "",
    panValidDate: null,
    gstNo: "",
    eccNumber: "",
    supplierMaster: "",
    exciseRange: "",
    exciseDivision: "",
    exciseCommissionerate: "",
    customerVendor: false,
    exciseTax: "",
    cstNo: "",
    lstNo: "",
    serviceTaxRegNo: "",

    msmeNo: "",
    msmeCategory: "",
    msmeCertDate: null,

    // Purchase Data
    purchaseOrg: "",
    orderCurrency: "",
    paymentTerm: "",
    purchaseGroup: "",
    plannedDeliveryDelay: "",
    schemaGroup: "",
    incoterm: "",
    incotermLocation: "",
    grInvoiceVerification: true,
    serviceInvoiceVerification: true,

    // Company Data
    companyCode: localStorage.getItem("Company_Code"),
    reconciliationAccount: "",
    headOfficeAccountNo: "",
    companyPaymentTerm: "",
    paymentMethod: "",
    houseBank: "",
    vendorAccountGroup: "Z001",
    withholdingTaxType: "",
    withholdingTaxCode: "",
    withholdingSubject: false,
    doubleInvoiceCheck: true,

    sectionCode: "",
    exemptionNumber: "",
    exemptionRate: "",
    exemptionTo: null,
    exemptionReason: "",
    withholdingTax: "",
    WithholdingTaxCode: "",
    thresholdAmount: "",
    currencyType: "",
    // Comment
    dataInfoComment: "",
  });
  const [showExemptionSection, setShowExemptionSection] = useState(false);
  useEffect(() => {
    const hasExemptionFields =
      form.sectionCode ||
      form.exemptionNumber ||
      form.exemptionRate ||
      form.exemptionTo ||
      form.exemptionReason ||
      form.withholdingTax ||
      form.WithholdingTaxCode ||
      form.thresholdAmount ||
      form.currencyType;

    const shouldShow =
      (!!form.withholdingTaxCode &&
        !!form.withholdingSubject &&
        !!form.doubleInvoiceCheck) ||
      !!hasExemptionFields;

    setShowExemptionSection(shouldShow);
  }, [
    form.withholdingTaxCode,
    form.withholdingSubject,
    form.doubleInvoiceCheck,
    form.sectionCode,
    form.exemptionNumber,
    form.exemptionRate,
    form.exemptionTo,
    form.exemptionReason,
    form.withholdingTax,
    form.WithholdingTaxCode,
    form.thresholdAmount,
    form.currencyType,
  ]);

  // -------- validation & input helpers --------
  const validateForm = () => {
    const newErrors = {};

    if (!form.country) newErrors.country = "Country is required.";
    if (!form.vendorType) newErrors.vendorType = "vendor Type is required.";
    if (!form.name1) newErrors.name1 = "Name1 is required.";
    if (!form.regionCode) newErrors.regionCode = "Region code is required.";
    if (!form.postalCity) newErrors.postalCity = "Postal code is required.";
    if (!form.vendorCategory)
      newErrors.vendorCategory = "Vendor Category is required.";
    if (!form.bankDetailsId)
      newErrors.bankDetailsId = "Bank Details ID is required.";
    if (!form.bankCountryKey)
      newErrors.bankCountryKey = "Bank Country Key is required.";
    //if (!form.bankKey) newErrors.bankKey = "Bank Key is required.";
    if (!form.bankAccountNo)
      newErrors.bankAccountNo = "Bank Account No. is required.";
    if (!form.bankName) newErrors.bankName = "Bank Name is required.";
    if (!form.IFSCOrSWIFTCode)
      newErrors.IFSCOrSWIFTCode = "IFSC/SWIFT Code is required.";
    if (!form.IECNumber) newErrors.IECNumber = "IEC Number is required.";
    if (
      !files.panCard &&
      fileLists.panCard.length === 0 &&
      form.country === "106"
    )
      newErrors.panCard = "PAN Card is required.";
    if (form.country === "106" && !form.pan) {
      newErrors.pan = "Permanent A/C No is required.";
    } else if (form.country !== "106" && !form.companyRegistrationNo) {
      newErrors.companyRegistrationNo = "Company Registration No is required.";
    }
    const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
    const TEN_DIGITS_RE = /^\d{10}$/;

    if (form.email1 && !EMAIL_RE.test(form.email1))
      newErrors.email1 = "Invalid email format.";
    if (form.email2 && !EMAIL_RE.test(form.email2))
      newErrors.email2 = "Invalid email format.";
    if (!form.email1) newErrors.email1 = "Email 1 is required.";
    if (form.mobile1 && !TEN_DIGITS_RE.test(String(form.mobile1)))
      newErrors.mobile1 = "Mobile number must be exactly 10 digits.";
    if (!form.mobile1) newErrors.mobile1 = "Mobile 1 is required.";
    if (form.mobile2 && !TEN_DIGITS_RE.test(String(form.mobile2)))
      newErrors.mobile2 = "Mobile number must be exactly 10 digits.";
    if (form.mobile3 && !TEN_DIGITS_RE.test(String(form.mobile3)))
      newErrors.mobile3 = "Mobile number must be exactly 10 digits.";

    const v = form.language;
    if (typeof v === "string" && v.length > 1) {
      newErrors.language = "Language Key must be a single character.";
    }

    if (!userDetails?.isVendor) {
      if (canEditPurchaseData) {
        if (!form.purchaseOrg)
          newErrors.purchaseOrg = "Purchase Org is required.";
        if (!form.orderCurrency)
          newErrors.orderCurrency = "Order Currency is required.";
        if (!form.paymentTerm)
          newErrors.paymentTerm = "Payment Term is required.";
        if (!form.purchaseGroup)
          newErrors.purchaseGroup = "Purchase Group is required.";
        if (
          form.plannedDeliveryDelay &&
          !/^\d+$/.test(String(form.plannedDeliveryDelay))
        ) {
          newErrors.plannedDeliveryDelay =
            "Planned Delivery Delay must be a number.";
        }
      }
    }
    if (!userDetails?.isVendor) {
      if (canEditCompanyDataValidation) {
        if (!form.companyCode)
          newErrors.companyCode = "Company Code is required.";
        if (!form.companyPaymentTerm)
          newErrors.companyPaymentTerm = "Company Payment Term is required.";
        if (!form.paymentMethod)
          newErrors.paymentMethod = "Payment Method is required.";
        if (!form.reconciliationAccount)
          newErrors.reconciliationAccount = "Reconciliation A/C is required.";
      }
    }

    if (form.vendorCategory !== "N/A") {
      if (!form.msmeNo) newErrors.msmeNo = "MSME No is required.";
      if (!form.msmeCategory)
        newErrors.msmeCategory = "MSME Category is required.";
      if (!form.msmeCertDate)
        newErrors.msmeCertDate = "MSME Certificate Date is required.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateFile = (file) => {
    const isValidType = SUPPORTED_MIME_TYPES.has(file.type);
    const isValidSize = file.size <= MAX_FILE_MB * 1024 * 1024;
    if (!isValidType) {
      NotificationManager.error("Please upload a PDF, PNG, JPG, or JPEG file.");
      return false;
    }
    if (!isValidSize) {
      NotificationManager.error(
        `File size should not exceed ${MAX_FILE_MB} MB.`
      );
      return false;
    }
    return true;
  };
  const handleCheckbox = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.checked }));

  const beforeUploadFor = (field) => (file) => {
    if (!validateFile(file)) return Upload.LIST_IGNORE;
    setFiles((prev) => ({ ...prev, [field]: file }));
    setFileLists((prev) => ({ ...prev, [field]: [file] }));
    if (errors[field]) {
      setErrors((prev) => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
    }
    return false;
  };

  const onRemoveFor = (field) => () => {
    setFiles((prev) => ({ ...prev, [field]: null }));
    setFileLists((prev) => ({ ...prev, [field]: [] }));
    setExistingFilePaths((prev) => ({ ...prev, [field]: "" }));
    return true;
  };

  const onChangeFor =
    (field) =>
    ({ fileList }) => {
      if (!fileList?.length) {
        setFileLists((prev) => ({ ...prev, [field]: [] }));
        return;
      }

      let lastFile = fileList[fileList.length - 1];
      const f = lastFile.originFileObj ? lastFile.originFileObj : lastFile;

      if (!validateFile(f)) {
        setFileLists((prev) => ({ ...prev, [field]: [] }));
        setFiles((prev) => ({ ...prev, [field]: null }));
        return;
      }
      let previewUrl = lastFile.url;
      if (f instanceof File && !previewUrl) {
        previewUrl = URL.createObjectURL(f);
      }
      const newAntdFile = {
        uid: lastFile.uid || String(Date.now()),
        name: lastFile.name || f.name,
        status: "done",
        url: previewUrl,
        originFileObj: f,
      };

      setFiles((prev) => ({ ...prev, [field]: f }));
      setFileLists((prev) => ({ ...prev, [field]: [newAntdFile] }));
    };

  const digitsOnly = (s = "") => s.replace(/\D/g, "");

  const handleInput = (e) => {
    const { name, value } = e.target;
    let v = value.trimStart();
    if (name === "pan") {
      if (value) {
        setForm((prev) => ({ ...prev, companyRegistrationNo: "" }));
      }
    }
    if (name === "companyRegistrationNo") {
      if (value) {
        setForm((prev) => ({ ...prev, pan: "" }));
      }
    }
    if (name === "mobile1" || name === "mobile2" || name === "mobile3") {
      v = digitsOnly(v).slice(0, 10);
    }
    if (name === "telephone" || name === "fax") {
      v = digitsOnly(v).slice(0, 15);
    }
    setForm((p) => ({ ...p, [name]: v }));
    if (errors[name]) {
      setErrors((prev) => {
        const updated = { ...prev };
        delete updated[name];
        return updated;
      });
    }
  };

  const handleDate = (field) => (d) => {
    if (d && errors[field]) {
      setErrors((prev) => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
    }

    setForm((prev) => ({
      ...prev,
      [field]: d ? d.format("YYYY-MM-DD") : null,
    }));
  };

  const handleSelect = useCallback(
    (field) => (value) => {
      setForm((prev) => ({ ...prev, [field]: value }));
      if (field === "country") {
        setFileLists((prev) => ({ ...prev, panCard: [] }));
        setExistingFilePaths((prev) => ({ ...prev, panCard: "" }));
      }
      const selectedCountry = countryList.find(
        (country) => country.id === value
      );
      if (selectedCountry?.ctr.trim() === "IN") {
        setIsIndiaSelected(true);

        if (form.pan) {
          setForm((prev) => ({ ...prev, companyRegistrationNo: "" }));
        }
      } else {
        setIsIndiaSelected(false);

        if (form.companyRegistrationNo) {
          setForm((prev) => ({ ...prev, pan: "" }));
        }
      }

      if (field === "withholdingTaxType") {
        if (!value) {
          setForm((prev) => ({ ...prev, withholdingTaxCode: "" }));
          setWithholdingCodeOptions1([]);
        } else {
          const selectedOption = withholdingTypeOptions.find(
            (option) => option.value === value
          );
          if (selectedOption) {
            fetchWithholdingTaxCodeOptions(selectedOption.key);
          }
        }
      }

      if (field === "withholdingTax") {
        if (!value) {
          setForm((prev) => ({ ...prev, WithholdingTaxCode: "" }));
          setWithholdingCodeOptions2([]);
        } else {
          const selectedOption = withholdingTypeOptions.find(
            (option) => option.value === value
          );
          if (selectedOption) {
            fetchWithholdingTaxCode2Options(selectedOption.key);
          }
        }
      }

      if (field === "vendorCategory") {
        if (value !== "N/A") {
          setForm((prev) => ({
            ...prev,
            msmeNo: "",
            msmeCategory: "",
            msmeCertDate: null,
          }));
        }

        setErrors((prev) => {
          const updated = { ...prev };
          delete updated.msmeNo;
          delete updated.msmeCategory;
          delete updated.msmeCertDate;
          return updated;
        });
      }

      if (errors[field]) {
        setErrors((prev) => {
          const updated = { ...prev };
          delete updated[field];
          return updated;
        });
      }
    },
    [errors, form, countryList, withholdingTypeOptions]
  );

  const buildOptions = (list, { value, label, key }) =>
    (list || [])
      .map((item) => {
        const v = value(item);
        const l = label(item);
        return {
          value: String(v ?? "").trim(),
          label: String(l ?? "").trim(),
          key: key ? key(item) : String(v ?? l ?? ""),
        };
      })
      .filter((o) => o.value && o.label)
      .sort((a, b) =>
        a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
      );

  const {
    countryOptions,
    regionalCodeOptions,
    languageOptions,
    typeOfVendorOptions,
    vendorCategoryOptions,
    titleOptions,
    bankCountryKeyOptions,
  } = useMemo(() => {
    return {
      bankCountryKeyOptions: buildOptions(countryList, {
        value: (c) => c?.id,
        label: (c) => c?.ctr,
        key: (c) => c?.ctr,
      }),
      countryOptions: buildOptions(countryList, {
        value: (c) => c?.id,
        label: (c) => c?.name,
        key: (c) => c?.ctr,
      }),
      regionalCodeOptions: buildOptions(regionalCodeList, {
        value: (r) => r?.regionCode,
        label: (r) => r?.description,
        key: (r) => r?.id,
      }),
      languageOptions: buildOptions(languageList, {
        value: (l) => l?.language,
        label: (l) => l?.nameOfLanguage,
        key: (l) => l?.id,
      }),
      typeOfVendorOptions: buildOptions(typeOfVendorList, {
        value: (t) => t?.id,
        label: (t) => t?.vendorType,
        key: (t) => t?.id,
      }),
      vendorCategoryOptions: buildOptions(vendorCategoryList, {
        value: (v) => v?.code,
        label: (v) => v?.description,
        key: (v) => v?.id,
      }),
      titleOptions: buildOptions(titleList, {
        value: (v) => v?.titleKey,
        label: (v) => v?.titleValue,
        key: (v) => v?.id,
      }),
    };
  }, [
    countryList,
    regionalCodeList,
    languageList,
    typeOfVendorList,
    vendorCategoryList,
    titleList,
  ]);

  const filterByLabelOrValue = (input, option) => {
    const text = `${option?.label ?? ""} ${option?.value ?? ""}`.toLowerCase();
    return text.includes(input.toLowerCase());
  };
  const handlePreview = async (file) => {
    const url = file.url || file.thumbUrl;
    if (url) {
      window.open(url, "_blank");
    }
  };
  const handleDownload = async (fileUrl) => {
    try {
      const response = await fetch(fileUrl);
      if (!response.ok) {
        throw new Error("Failed to fetch the file");
      }
      const blob = await response.blob();
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.href = url;
      link.download = fileUrl.split("/").pop();
      document.body.appendChild(link);
      link.click();
      URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } catch (error) {
      console.error("Failed to download the file:", error);
    }
  };
  const truncateName = (name, length = 12) => {
    return name.length > length ? name.slice(0, length) + "..." : name;
  };

  useEffect(() => {
    let mounted = true;

    const fetchAllDropdowns = async () => {
      try {
        const [countryRes, langRes, tovRes, vendorCatRes, titleRes] =
          await Promise.all([
            VendorOnboardingService.getCountryMaster(),
            VendorOnboardingService.getLanguageMaster(),
            VendorOnboardingService.getTypeOfVendorMaster(),
            VendorOnboardingService.getVendorCategoryMaster(),
            VendorOnboardingService.getTitleMaster(),
          ]);

        const pickList = (res) =>
          Array.isArray(res?.data?.responseData)
            ? res.data.responseData
            : Array.isArray(res?.data)
            ? res.data
            : [];

        if (!mounted) return;

        const langList = pickList(langRes).map((l) => ({
          ...l,
          language: String(l.language ?? "").trim(),
          nameOfLanguage: String(l.nameOfLanguage ?? "").trim(),
        }));

        setLanguageList(langList);

        if (langList.length === 1 && !form.language) {
          setForm((prev) => ({
            ...prev,
            language: langList[0].language,
          }));
        }

        setCountryList(pickList(countryRes));
        // setLanguageList(pickList(langRes));
        setTypeOfVendorList(pickList(tovRes));

        setVendorCategoryList(pickList(vendorCatRes));
        setTitleList(pickList(titleRes));

        if (userDetails?.isEmployee) {
          const [
            purchaseOrgRes,
            purchaseGroupRes,
            orderCurrencyRes,
            paymentTermRes,
            schemaGroupRes,
            paymentMethodRes,
            reconAccountRes,
            houseBankRes,
            withholdingRes,
          ] = await Promise.all([
            VendorOnboardingService.getPurchaseOrg(),
            VendorOnboardingService.getPurchaseGroupMaster(),
            VendorOnboardingService.getOrderCurrency(),
            VendorOnboardingService.getPaymentTerms(),
            VendorOnboardingService.getSchemaGroup(),
            VendorOnboardingService.getPaymentMethod(),
            VendorOnboardingService.getReconciliationAccount(),
            VendorOnboardingService.getHouseBank({
              companyCode: localStorage.getItem("Company_Code"),
            }),
            VendorOnboardingService.getWithholdingTaxType(),
          ]);

          if (!mounted) return;

          setPurchaseOrgOptions(
            pickList(purchaseOrgRes).map((item) => ({
              value: String(item.id),
              label: item.purchOrgDescr,
            }))
          );
          setPurchaseGroupOptions(
            pickList(purchaseGroupRes).map((item) => ({
              value: String(item.id),
              label: item.description,
            }))
          );
          setOrderCurrencyOptions(
            pickList(orderCurrencyRes).map((item) => ({
              value: String(item.id),
              label: item.longText,
            }))
          );
          setPaymentTermOptions(
            pickList(paymentTermRes).map((item) => ({
              value: String(item.id),
              label: item.ownExplanation,
            }))
          );

          setSchemaGroupOptions(
            pickList(schemaGroupRes).map((item) => ({
              value: String(item.id),
              label: item.description,
            }))
          );
          setPaymentMethodOptions(
            pickList(paymentMethodRes).map((item) => ({
              value: String(item.id),
              label: item.methodDescription,
            }))
          );
          setReconAccountOptions(
            pickList(reconAccountRes).map((item) => ({
              value: String(item.id),
              label: item.longText,
            }))
          );
          setHouseBankOptions(
            pickList(houseBankRes).map((item) => ({
              value: String(item.id),
              label: `${item.house_Bank} - ${item.bankName}`,
            }))
          );
          setWithholdingTypeOptions(
            pickList(withholdingRes).map((item) => ({
              value: String(item.id),
              label: item.name,
              key: item.wTaxType,
            }))
          );
        }
      } catch (err) {
        console.error(err);
        NotificationManager.error("Failed to load dropdown data");

        if (!mounted) return;
        setCountryList([]);
        setLanguageList([]);
        setTypeOfVendorList([]);
        setVendorCategoryList([]);
        setTitleList([]);

        if (userDetails?.isEmployee) {
          setPurchaseOrgOptions([]);
          setPurchaseGroupOptions([]);
          setOrderCurrencyOptions([]);
          setPaymentTermOptions([]);
          setSchemaGroupOptions([]);
          setPaymentMethodOptions([]);
          setReconAccountOptions([]);
          setHouseBankOptions([]);
          setWithholdingTypeOptions([]);
        }
      }
    };

    fetchAllDropdowns();
    return () => {
      mounted = false;
    };
  }, [userDetails?.isEmployee]);

  useEffect(() => {
    if (!form.country) {
      if (regionalCodeList.length > 0) {
        setRegionalCodeList([]);
      }
      return;
    }

    if (!countryOptions || countryOptions.length === 0) return;

    const selectedCountry = countryOptions.find(
      (opt) => opt.value === form.country
    );

    if (!selectedCountry?.key) {
      if (regionalCodeList.length > 0) {
        setRegionalCodeList([]);
      }
      return;
    }

    if (
      regionalCodeList.length > 0 &&
      regionalCodeList[0]?.countryKey === selectedCountry.key
    ) {
      return;
    }

    VendorOnboardingService.getRegionalCodeMaster(selectedCountry.key)
      .then((res) => {
        if (mountedRef.current) {
          const list = Array.isArray(res?.data) ? res.data : [];

          const withCountry = list.map((r) => ({
            ...r,
            countryKey: selectedCountry.key,
          }));
          setRegionalCodeList(withCountry);
        }
      })
      .catch(() => {
        if (regionalCodeList.length > 0) {
          setRegionalCodeList([]);
        }
      });
  }, [form.country, countryOptions]);

  const selectedRegion = regionalCodeOptions.find(
    (option) => option.value === form.regionCode
  );

  const regionLabel = selectedRegion ? selectedRegion.label : "Select Region *";
  useEffect(() => {
    // For first pair: withholdingTaxType → withholdingTaxCode
    if (form.withholdingTaxType && withholdingTypeOptions.length > 0) {
      const selectedOption = withholdingTypeOptions.find(
        (opt) => opt.value === form.withholdingTaxType
      );

      if (selectedOption) {
        fetchWithholdingTaxCodeOptions(
          selectedOption.key,
          form.withholdingTaxCode
        );
      }
    }

    // For second pair: withholdingTax → WithholdingTaxCode
    if (form.withholdingTax && withholdingTypeOptions.length > 0) {
      const selectedOption2 = withholdingTypeOptions.find(
        (opt) => opt.value === form.withholdingTax
      );

      if (selectedOption2) {
        fetchWithholdingTaxCode2Options(
          selectedOption2.key,
          form.WithholdingTaxCode
        );
      }
    }
  }, [form.withholdingTaxType, form.withholdingTax, withholdingTypeOptions]);

  const fetchWithholdingTaxCodeOptions = async (WTaxType, preselectedCode) => {
    try {
      const response = await VendorOnboardingService.getWithholdingTaxCode(
        WTaxType
      );
      const options = response?.data?.responseData || [];

      const mapped = options.map((item) => ({
        value: String(item.id),
        label: item.name,
      }));

      setWithholdingCodeOptions1(mapped);

      if (
        preselectedCode &&
        mapped.some((opt) => opt.value === preselectedCode)
      ) {
        setForm((prev) => ({ ...prev, withholdingTaxCode: preselectedCode }));
      }
    } catch (error) {
      console.error("Error fetching withholding tax codes:", error);
      setWithholdingCodeOptions1([]);
    }
  };
  const fetchWithholdingTaxCode2Options = async (WTaxType, preselectedCode) => {
    try {
      const response = await VendorOnboardingService.getWithholdingTaxCode(
        WTaxType
      );
      const options = response?.data?.responseData || [];

      const mapped = options.map((item) => ({
        value: String(item.id),
        label: item.name,
      }));

      setWithholdingCodeOptions2(mapped);

      if (
        preselectedCode &&
        mapped.some((opt) => opt.value === preselectedCode)
      ) {
        setForm((prev) => ({ ...prev, WithholdingTaxCode: preselectedCode }));
      }
    } catch (error) {
      console.error("Error fetching withholding tax codes:", error);
      setWithholdingCodeOptions2([]);
    }
  };

  const saveVendorData = async () => {
    setLoading(true);
    // const companyCodeFromLS = localStorage.getItem("Company_Code");
    // const companyCode = userDetails?.companyCode || companyCodeFromLS;
    if (!validateForm()) {
      NotificationManager.error("Please fill all required fields.");
      setLoading(false);
      return;
    }
    try {
      const fd = new FormData();

      // Append VendorDetails to the FormData
      fd.append("VendorDetails.VendorId", String(form.vendorId || vendorId));
      fd.append("VendorDetails.VendorType", form.vendorType ?? "");
      fd.append("VendorDetails.TitleKey", form.titleKey ?? "");
      fd.append("VendorDetails.Name1", form.name1 ?? "");
      fd.append("VendorDetails.Name2", form.name2 ?? "");
      fd.append("VendorDetails.Name3", form.name3 ?? "");
      fd.append("VendorDetails.Name4", form.name4 ?? "");
      fd.append("VendorDetails.COName", form.coName ?? "");

      // Append Address data
      fd.append("VendorDetails.CountryCode", form.country ?? "");
      fd.append("VendorDetails.RegionCode", form.regionCode ?? "");
      fd.append("VendorDetails.Location", form.location ?? "");
      fd.append("VendorDetails.District", form.district ?? "");
      fd.append("VendorDetails.PostalCode", form.postalCity ?? "");
      fd.append("VendorDetails.CityName", form.cityName ?? "");
      fd.append("VendorDetails.Street1", form.street1 ?? "");
      fd.append("VendorDetails.Street2", form.street2 ?? "");
      fd.append("VendorDetails.Street3", form.street3 ?? "");
      fd.append("VendorDetails.Street4", form.street4 ?? "");

      // Append Contact data
      fd.append("VendorDetails.LanguageKey", form.language ?? "");
      fd.append("VendorDetails.VendorCategory", form.vendorCategory ?? "");
      fd.append("VendorDetails.TransportationZone", form.transportZone ?? "");
      fd.append(
        "VendorDetails.CertificateDate",
        formatDate(form.certificateDate)
      );
      fd.append("VendorDetails.TelephoneNo", form.telephone ?? "");
      fd.append("VendorDetails.FaxNo", form.fax ?? "");
      fd.append("VendorDetails.Mobile1", form.mobile1 ?? "");
      fd.append("VendorDetails.Mobile2", form.mobile2 ?? "");
      fd.append("VendorDetails.Mobile3", form.mobile3 ?? "");
      fd.append("VendorDetails.Email1", form.email1 ?? "");
      fd.append("VendorDetails.Email2", form.email2 ?? "");

      // Bank + Legal/Tax
      fd.append("BankDetails.BankDetailsID", form.bankDetailsId ?? "0001");
      fd.append("BankDetails.BankCountryKey", form.bankCountryKey ?? "");
      fd.append("BankDetails.BankKey", "1");
      fd.append("BankDetails.BankAccountNo", form.bankAccountNo ?? "");
      fd.append("BankDetails.BankName", form.bankName ?? "");
      fd.append("BankDetails.IFSC_SWIFT_Code", form.IFSCOrSWIFTCode ?? "");
      fd.append("BankDetails.IEC_Number", form.IECNumber ?? "");
      fd.append("BankDetails.PermanentAccountNo", form.pan ?? "");
      fd.append(
        "BankDetails.companyRegistrationNo",
        form.companyRegistrationNo ?? ""
      );
      fd.append("BankDetails.PANReferenceNo", form.panRefNo ?? "");
      fd.append("BankDetails.PANValidFromDate", formatDate(form.panValidDate));
      fd.append("BankDetails.GSTNo", form.gstNo ?? "");
      fd.append("BankDetails.ECCNumber", form.eccNumber ?? "");
      fd.append("BankDetails.SupplierMaster", form.supplierMaster ?? "");
      fd.append("BankDetails.CustomerVendor", form.customerVendor ?? "");
      fd.append("BankDetails.CentralSalesTaxNumber", form.cstNo ?? "");
      fd.append("BankDetails.LocalSalesTaxNumber", form.lstNo ?? "");
      fd.append("BankDetails.ServiceTaxRegNo", form.serviceTaxRegNo ?? "");

      // MSME
      fd.append("VendorDetails.MSMENo", form.msmeNo ?? "");
      fd.append("VendorDetails.MSMECategory", form.msmeCategory ?? "");
      fd.append(
        "VendorDetails.MSMECertificatedate",
        formatDate(form.msmeCertDate)
      );

      // Pan Card
      if (files.panCard) {
        fd.append("PanCard", files.panCard, files.panCard.name);
      } else {
        fd.append("PanCardPath", existingFilePaths.panCard || "");
      }

      // GST Certificate
      if (files.gstCertificate) {
        fd.append(
          "GstCertificate",
          files.gstCertificate,
          files.gstCertificate.name
        );
      } else {
        fd.append("GstCertificatePath", existingFilePaths.gstCertificate || "");
      }

      // Cancelled Cheque
      if (files.cancelledCheque) {
        fd.append(
          "CancelledCheque",
          files.cancelledCheque,
          files.cancelledCheque.name
        );
      } else {
        fd.append(
          "CancelledChequePath",
          existingFilePaths.cancelledCheque || ""
        );
      }

      // Purchase Data
      if (showPurchaseData) {
        fd.append("VendorPurchasingData.PurchaseOrg", form.purchaseOrg ?? "");
        fd.append(
          "VendorPurchasingData.OrderCurrency",
          form.orderCurrency ?? ""
        );
        fd.append("VendorPurchasingData.PaymentTerms", form.paymentTerm ?? "");
        fd.append(
          "VendorPurchasingData.PurchaseGroup",
          form.purchaseGroup ?? ""
        );
        fd.append(
          "VendorPurchasingData.PlannedDelDays",
          form.plannedDeliveryDelay ?? ""
        );
        fd.append("VendorPurchasingData.SchemaGroup", form.schemaGroup ?? "");
        fd.append("VendorPurchasingData.Incoterms", form.incoterm ?? "");
        fd.append(
          "VendorPurchasingData.IncotermsLocation",
          form.incotermLocation ?? ""
        );
        fd.append(
          "VendorPurchasingData.GRBasedInvoiceVerification",
          String(!!form.grInvoiceVerification)
        );
        fd.append(
          "VendorPurchasingData.ServiceBasedInvoiceVerification",
          String(!!form.serviceInvoiceVerification)
        );
      }

      // Company Data
      if (showCompanyData) {
        fd.append("VendorCompanyData.CompanyCode", form.companyCode ?? "");
        fd.append(
          "VendorCompanyData.ReconciliationAccount",
          form.reconciliationAccount ?? ""
        );
        fd.append(
          "VendorCompanyData.HOAccountNo",
          form.headOfficeAccountNo ?? ""
        );
        fd.append(
          "VendorCompanyData.PaymentTerm",
          form.companyPaymentTerm ?? ""
        );
        fd.append("VendorCompanyData.PaymentMethod", form.paymentMethod ?? "");
        fd.append("VendorCompanyData.HouseBank", form.houseBank ?? "");
        fd.append(
          "VendorCompanyData.VendorAccountGroup",
          form.vendorAccountGroup ?? "Z001"
        );
        fd.append(
          "VendorCompanyData.WithholdingTaxType1",
          form.withholdingTaxType ?? ""
        );
        fd.append(
          "VendorCompanyData.withholdingTaxCode1",
          form.withholdingTaxCode ?? ""
        );
        fd.append(
          "VendorCompanyData.SubjectToWithholdingTax1",
          String(!!form.withholdingSubject)
        );
        fd.append(
          "VendorCompanyData.CheckDoubleInvoice",
          String(!!form.doubleInvoiceCheck)
        );
        fd.append("VendorCompanyData.sectionCode", form.sectionCode ?? "");
        fd.append(
          "VendorCompanyData.exemptionNumber",
          form.exemptionNumber ?? ""
        );
        fd.append("VendorCompanyData.exemptionRate", form.exemptionRate ?? "");
        fd.append(
          "VendorCompanyData.exemptionTo",
          formatDate(form.exemptionTo)
        );
        fd.append(
          "VendorCompanyData.exemptionReason",
          form.exemptionReason ?? ""
        );
        fd.append(
          "VendorCompanyData.withholdingTaxType",
          form.withholdingTax ?? ""
        );
        fd.append(
          "VendorCompanyData.WithholdingTaxCode",
          form.WithholdingTaxCode ?? ""
        );

        fd.append(
          "VendorCompanyData.thresholdAmount",
          form.thresholdAmount ?? ""
        );
        fd.append("VendorCompanyData.currency", form.currencyType ?? "");
      }

      // Call save API
      const response = await VendorOnboardingService.saveVendorInformation(fd);
      NotificationManager.success(
        response?.data?.message || "Vendor information saved."
      );
      setLoading(false);
      if (userDetails?.isVendor) {
        navigate("/vendorData");
        return;
      }
      if (response?.data?.success) {
        return { success: true };
      } else {
        return { success: false };
      }
    } catch (err) {
      console.error("Error while saving vendor:", err);
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to save vendor information.";
      NotificationManager.error(errorMessage);
      setLoading(false);
      return { success: false };
    }
  };

  useEffect(() => {
    let mounted = true;

    const run = async () => {
      const companyCodeFromLS = localStorage.getItem("Company_Code");
      const effectiveVendorId = vendorId;
      const companyCode = userDetails?.companyCode || companyCodeFromLS;

      if (!effectiveVendorId) {
        return;
      }

      try {
        setLoading(true);

        if (submitNow) {
          const ref =
            await VendorOnboardingService.getVendorFromDetailsByVendorId({
              vendorId,
              companyCode,
            });

          const apiAfter = ref?.data?.responseData;
          if (apiAfter && mounted) {
            const normalized = normalizeVendorApiToForm(apiAfter);
            setForm((prev) => ({ ...prev, ...normalized }));
            setInitialForm(normalized);
            setCanFullEdit(ref.data.responseData?.canFullEdit);

            const { fileLists: fl, paths } = pickAttachmentSlots(
              apiAfter.vendorAttachments || []
            );
            setFileLists(fl);
            setExistingFilePaths(paths);
            setFiles({
              panCard: null,
              gstCertificate: null,
              cancelledCheque: null,
            });
            setFiles({
              panCard: null,
              gstCertificate: null,
              cancelledCheque: null,
            });
          }
        } else {
          const resp =
            await VendorOnboardingService.getVendorFromDetailsByVendorId({
              vendorId: effectiveVendorId,
              companyCode,
            });
          const api = resp?.data?.responseData;
          setCanFullEdit(resp.data.responseData?.canFullEdit);
          setUserRoleName(api?.roleName || "");
          if (!api) {
            if (!isEditMode) {
              NotificationManager.error(
                "Data is still pending to be updated by the vendor."
              );
            }
            return;
          }
          if (!mounted) return;

          const normalized = normalizeVendorApiToForm(api);
          setForm((prev) => ({ ...prev, ...normalized }));
          setInitialForm(normalized);

          if (normalized.withholdingTaxType) {
            const selectedOption = withholdingTypeOptions.find(
              (opt) => opt.value === normalized.withholdingTaxType
            );
            if (selectedOption) {
              fetchWithholdingTaxCodeOptions(selectedOption.key);
            }
          }
          const { fileLists: fl, paths } = pickAttachmentSlots(
            api.vendorAttachments || []
          );
          setFileLists(fl);
          setExistingFilePaths(paths);

          setFiles({
            panCard: null,
            gstCertificate: null,
            cancelledCheque: null,
          });
        }
      } catch (err) {
        console.error("Vendor fetch/save error:", err);
        NotificationManager.error(
          submitNow
            ? "Failed to save vendor information."
            : "Failed to load vendor details."
        );
        setSubmitNow(false);
      } finally {
        if (mounted) setLoading(false);
        setSubmitNow(false);
      }
    };

    run();
    return () => {
      mounted = false;
    };
  }, [userDetails, submitNow]);

  useEffect(() => {
    if (form.withholdingTaxType && withholdingTypeOptions.length > 0) {
      const selectedOption = withholdingTypeOptions.find(
        (opt) => opt.value === form.withholdingTaxType
      );
      if (selectedOption) {
        fetchWithholdingTaxCodeOptions(selectedOption.key);
      }
    }
  }, [form.withholdingTaxType, withholdingTypeOptions]);
  //-------- handle vendor action --------
  const handleVendorAction = async (action) => {
    try {
      if (action === "Approve") {
        const isFormValid = validateForm();

        if (!isFormValid) {
          NotificationManager.error("Please fill all required fields.");
          return;
        }
        const shouldCallSave =
          role !== "finance hod" || (role === "finance hod" && canFullEdit);

        // On Approve → trigger save first if user is NOT Finance HOD
        if (shouldCallSave) {
          const saveResponse = await saveVendorData();
          console.log(saveResponse, "saveResponse");

          if (!saveResponse?.success) {
            return;
          }

          await new Promise((resolve) => {
            const check = setInterval(() => {
              if (!loading && !submitNow) {
                clearInterval(check);
                resolve();
              }
            }, 300);
          });
        }
      } else if (action === "Reject") {
        if (!form.dataInfoComment || form.dataInfoComment.trim() === "") {
          setErrors((prev) => ({
            ...prev,
            dataInfoComment: "Comment is required.",
          }));
          NotificationManager.error("Comment is required.");
          return;
        }
      }

      setActionLoading(action);

      const payload = {
        vendorId,
        companyCode: localStorage.getItem("Company_Code"),
        comment: form.dataInfoComment,
        action,
      };

      const response =
        await VendorOnboardingService.VendorFormApproveRejectByRole(payload);

      if (response?.status === 200 && response.data?.success) {
        NotificationManager.success(
          response.data.message || `Vendor ${action}d successfully!`
        );
        navigate("/VendorManagement");
      } else {
        NotificationManager.error(
          response.data?.message || `Failed to ${action} vendor`
        );
      }
    } catch (err) {
      NotificationManager.error(`Failed to ${action} vendor`);
      console.error(err);
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className="app-content content VendorOnboarding">
      <div className="content-wrapper">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h5 className="content-header-title d-inline-block">
            {isView ? "View Vendor Details" : "Edit Vendor Details"}
          </h5>
          <div className="pt-0 reset_save_btn justify-content-start">
            <Button
              className="backBtn"
              onClick={() => {
                if (userDetails?.isEmployee) {
                  navigate("/VendorManagement");
                } else {
                  navigate("/vendorData");
                }
              }}
            >
              Back
            </Button>
          </div>
        </div>

        <Collapse
          defaultActiveKey={["1", "2", "3", "4", "5", "6"]}
          bordered={false}
          expandIconPosition="end"
          expandIcon={({ isActive }) =>
            isActive ? (
              <MinusCircleOutlined style={{ fontSize: 18, color: "#3CAFDA" }} />
            ) : (
              <PlusCircleOutlined style={{ fontSize: 18, color: "#3CAFDA" }} />
            )
          }
          style={{ backgroundColor: "transparent", position: "relative" }}
        >
          {/* Vendor Information */}
          <Panel
            header="Vendor Information  (All fields marked with * are mandatory.)"
            key="1"
          >
            <Row gutter={16}>
              <Col md={12}>
                <div className="card my-3" style={{ minHeight: "90%" }}>
                  <div className="card-content">
                    <div className="card-body">
                      <h6 className="fw-bold">Vendor Name</h6>
                      <Row gutter={[8, 8]}>
                        <Col span={12}>
                          <FormSelect
                            disabled={disable}
                            name="vendorType"
                            placeholder="Vendor Type *"
                            value={form.vendorType}
                            onChange={handleSelect("vendorType")}
                            options={typeOfVendorOptions}
                            showSearch
                            filterOption={filterByLabelOrValue}
                            error={errors.vendorType}
                          />
                        </Col>
                        <Col span={12}>
                          <FormSelect
                            disabled={disable}
                            name="titleKey"
                            placeholder="Title Key"
                            value={form.titleKey}
                            onChange={handleSelect("titleKey")}
                            options={titleOptions}
                            showSearch
                            filterOption={filterByLabelOrValue}
                          />
                        </Col>
                        <Col span={12}>
                          <FormInput
                            disabled={disable}
                            name="name1"
                            placeholder="Name1 *"
                            value={form.name1}
                            onChange={handleInput}
                            maxLength={35}
                            className={`w-100 ${
                              errors.Name1 ? "input_error" : ""
                            }`}
                            error={errors.name1}
                          />
                        </Col>
                        <Col span={12}>
                          <FormInput
                            disabled={disable}
                            name="name2"
                            placeholder="Name2"
                            value={form.name2}
                            onChange={handleInput}
                            maxLength={35}
                          />
                        </Col>
                        <Col span={12}>
                          <FormInput
                            disabled={disable}
                            name="name3"
                            placeholder="Name3"
                            value={form.name3}
                            onChange={handleInput}
                            maxLength={35}
                          />
                        </Col>
                        <Col span={12}>
                          <FormInput
                            disabled={disable}
                            name="name4"
                            placeholder="Name4"
                            value={form.name4}
                            onChange={handleInput}
                            maxLength={35}
                          />
                        </Col>
                        <Col span={12}>
                          <FormInput
                            disabled={disable}
                            name="coName"
                            placeholder="CO Name"
                            value={form.coName}
                            onChange={handleInput}
                            maxLength={30}
                          />
                        </Col>
                      </Row>
                    </div>
                  </div>
                </div>
              </Col>

              <Col md={12}>
                <div className="card my-3">
                  <div className="card-content">
                    <div className="card-body">
                      <h6 className="fw-bold">Vendor Address</h6>
                      <Row gutter={[8, 8]}>
                        <Col span={12}>
                          <FormInput
                            disabled={disable}
                            name="street1"
                            placeholder="Street 1"
                            value={form.street1}
                            onChange={handleInput}
                            maxLength={60}
                          />
                        </Col>
                        <Col span={12}>
                          <FormInput
                            disabled={disable}
                            name="street2"
                            placeholder="Street 2"
                            value={form.street2}
                            onChange={handleInput}
                            maxLength={100}
                          />
                        </Col>
                        <Col span={12}>
                          <FormInput
                            disabled={disable}
                            name="street3"
                            placeholder="Street 3"
                            value={form.street3}
                            onChange={handleInput}
                            maxLength={40}
                          />
                        </Col>
                        <Col span={12}>
                          <FormInput
                            disabled={disable}
                            name="street4"
                            placeholder="Street 4"
                            value={form.street4}
                            onChange={handleInput}
                            maxLength={40}
                          />
                        </Col>
                        <Col span={12}>
                          <FormInput
                            disabled={disable}
                            name="location"
                            placeholder="Location"
                            value={form.location}
                            onChange={handleInput}
                            maxLength={40}
                          />
                        </Col>
                        <Col span={12}>
                          <FormInput
                            disabled={disable}
                            name="district"
                            placeholder="District"
                            value={form.district}
                            onChange={handleInput}
                            maxLength={35}
                          />
                        </Col>
                        <Col span={12}>
                          <FormInput
                            disabled={disable}
                            name="postalCity"
                            placeholder="Postal Code *"
                            value={form.postalCity}
                            onChange={handleInput}
                            maxLength={10}
                            error={errors.postalCity}
                          />
                        </Col>
                        <Col span={12}>
                          <FormInput
                            disabled={disable}
                            name="cityName"
                            placeholder="City Name"
                            value={form.cityName}
                            onChange={handleInput}
                            maxLength={40}
                          />
                        </Col>
                        <Col span={12}>
                          <FormSelect
                            name="country"
                            allowClear
                            showSearch
                            placeholder="Country Code *"
                            value={form.country || undefined}
                            onChange={handleSelect("country")}
                            className={`w-100 ${
                              errors.country ? "input_error" : ""
                            }`}
                            options={countryOptions}
                            filterOption={filterByLabelOrValue}
                            error={errors.country}
                            disabled={disable}
                          />
                        </Col>
                        <Col span={12}>
                          <FormSelect
                            allowClear
                            showSearch
                            name="regionCode"
                            placeholder={regionLabel || "Select Region *"}
                            value={form.regionCode || undefined}
                            onChange={handleSelect("regionCode")}
                            className={`w-100 ${
                              errors.regionCode ? "input_error" : ""
                            }`}
                            options={regionalCodeOptions}
                            filterOption={filterByLabelOrValue}
                            error={errors.regionCode}
                            disabled={disable}
                          />
                        </Col>
                      </Row>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
          </Panel>
          {/* Contact Information */}
          <Panel
            header="Contact Information (All fields marked with * are mandatory.)"
            key="2"
          >
            <div className="card my-3">
              <div className="card-content">
                <div className="card-body">
                  <h6 className="fw-bold">Contact Details</h6>
                  <Row gutter={[8, 8]}>
                    <Col xs={12} sm={12} md={12} lg={4}>
                      <FormSelect
                        name="language"
                        placeholder="Language"
                        value={form.language}
                        onChange={handleSelect("language")}
                        options={languageOptions}
                        showSearch
                        filterOption={filterByLabelOrValue}
                        disabled={disable}
                      />
                    </Col>

                    <Col xs={12} sm={12} md={12} lg={4}>
                      <FormSelect
                        name="vendorCategory"
                        placeholder="Vendor Cat. *"
                        value={form.vendorCategory}
                        onChange={handleSelect("vendorCategory")}
                        options={vendorCategoryOptions}
                        showSearch
                        filterOption={filterByLabelOrValue}
                        disabled={disable}
                        className={`w-100 ${
                          errors.vendorCategory ? "input_error" : ""
                        }`}
                        error={errors.vendorCategory}
                      />
                    </Col>

                    <Col xs={12} sm={12} md={12} lg={4}>
                      <FormInput
                        disabled={disable}
                        name="transportZone"
                        placeholder="Trans. Zone"
                        value={form.transportZone}
                        onChange={handleInput}
                        maxLength={10}
                      />
                    </Col>
                    <Col xs={12} sm={12} md={12} lg={4}>
                      <DatePicker
                        className="w-100"
                        placeholder="Certificate Date"
                        value={
                          form.certificateDate
                            ? moment(form.certificateDate, "YYYY-MM-DD")
                            : null
                        }
                        onChange={handleDate("certificateDate")}
                        disabled={disable}
                        disabledDate={(current) =>
                          current && current > moment()
                        }
                        format="DD-MM-YYYY"
                      />
                    </Col>
                    <Col xs={12} sm={12} md={12} lg={4}>
                      <FormInput
                        disabled={disable}
                        name="telephone"
                        placeholder="Telephone No."
                        value={form.telephone}
                        onChange={handleInput}
                        maxLength={11}
                      />
                    </Col>
                    <Col xs={12} sm={12} md={12} lg={4}>
                      <FormInput
                        disabled={disable}
                        name="fax"
                        placeholder="Fax No."
                        value={form.fax}
                        onChange={handleInput}
                        maxLength={10}
                      />
                    </Col>
                    <Col xs={12} sm={12} md={12} lg={4}>
                      <FormInput
                        disabled={disable}
                        name="mobile1"
                        placeholder="Mobile No. 1 *"
                        value={form.mobile1}
                        onChange={handleInput}
                        error={errors.mobile1}
                        className={errors.mobile1 ? "input_error" : ""}
                        maxLength={30}
                      />
                    </Col>
                    <Col xs={12} sm={12} md={12} lg={4}>
                      <FormInput
                        disabled={disable}
                        name="mobile2"
                        placeholder="Mobile No. 2"
                        value={form.mobile2}
                        onChange={handleInput}
                        error={errors.mobile2}
                        className={errors.mobile2 ? "input_error" : ""}
                        maxLength={30}
                      />
                    </Col>
                    <Col xs={12} sm={12} md={12} lg={4}>
                      <FormInput
                        disabled={disable}
                        name="mobile3"
                        placeholder="Mobile No. 3"
                        value={form.mobile3}
                        onChange={handleInput}
                        error={errors.mobile3}
                        className={errors.mobile3 ? "input_error" : ""}
                        maxLength={30}
                      />
                    </Col>
                    <Col xs={12} sm={12} md={12} lg={4}>
                      <FormInput
                        disabled={disable}
                        name="email1"
                        placeholder="Email ID 1 *"
                        value={form.email1}
                        onChange={handleInput}
                        error={errors.email1}
                        className={errors.email1 ? "input_error" : ""}
                        maxLength={241}
                      />
                    </Col>
                    <Col xs={12} sm={12} md={12} lg={4}>
                      <FormInput
                        disabled={disable}
                        name="email2"
                        placeholder="Email ID 2"
                        value={form.email2}
                        onChange={handleInput}
                        error={errors.email2}
                        className={errors.email2 ? "input_error" : ""}
                        maxLength={241}
                      />
                    </Col>
                  </Row>
                </div>
              </div>
            </div>
          </Panel>
          {/* Bank Information */}
          <Panel
            header="Bank Information (All fields marked with * are mandatory.)"
            key="3"
          >
            <div className="card my-3">
              <div className="card-content">
                <div className="card-body">
                  <h6 className="fw-bold">Bank Details</h6>
                  <Row gutter={[8, 8]}>
                    <Col xs={12} sm={12} md={12} lg={4}>
                      <FormInput
                        disabled={disable}
                        name="bankDetailsId"
                        placeholder="Bank Details ID *"
                        value={form.bankDetailsId}
                        onChange={handleInput}
                        className={errors.bankDetailsId ? "input_error" : ""}
                        error={errors.bankDetailsId}
                        maxLength={4}
                      />
                    </Col>
                    <Col xs={12} sm={12} md={12} lg={4}>
                      <FormSelect
                        name="bankCountryKey"
                        allowClear
                        showSearch
                        placeholder="Bank Country Key *"
                        value={form.bankCountryKey}
                        onChange={handleSelect("bankCountryKey")}
                        className={errors.bankCountryKey ? "input_error" : ""}
                        error={errors.bankCountryKey}
                        options={bankCountryKeyOptions}
                        filterOption={filterByLabelOrValue}
                        disabled={disable}
                      />
                    </Col>
                    {/* <Col xs={12} sm={12} md={12} lg={4}>
                      <FormInput
                        disabled={disable}
                        name="bankKey"
                        placeholder="Bank Key *"
                        value={form.bankKey}
                        onChange={handleInput}
                        maxLength={15}
                        className={errors.bankKey ? "input_error" : ""}
                        error={errors.bankKey}
                      />
                    </Col> */}
                    <Col xs={12} sm={12} md={12} lg={4}>
                      <FormInput
                        disabled={disable}
                        name="bankAccountNo"
                        placeholder="Bank Account No *"
                        value={form.bankAccountNo}
                        maxLength={18}
                        onChange={handleInput}
                        className={errors.bankAccountNo ? "input_error" : ""}
                        error={errors.bankAccountNo}
                      />
                    </Col>
                    <Col xs={12} sm={12} md={12} lg={4}>
                      <FormInput
                        disabled={disable}
                        name="bankName"
                        placeholder="Bank Name *"
                        value={form.bankName}
                        onChange={handleInput}
                        className={errors.bankName ? "input_error" : ""}
                        error={errors.bankName}
                        maxLength={100}
                      />
                    </Col>
                    <Col xs={12} sm={12} md={12} lg={4}>
                      <FormInput
                        disabled={disable}
                        name="IFSCOrSWIFTCode"
                        placeholder="IFSC/SWIFT Code *"
                        value={form.IFSCOrSWIFTCode}
                        onChange={handleInput}
                        maxLength={15}
                        className={errors.IFSCOrSWIFTCode ? "input_error" : ""}
                        error={errors.IFSCOrSWIFTCode}
                      />
                    </Col>
                    <Col xs={12} sm={12} md={12} lg={4}>
                      <FormInput
                        disabled={disable}
                        name="IECNumber"
                        placeholder="IEC Number *"
                        value={form.IECNumber}
                        onChange={handleInput}
                        maxLength={10}
                        className={errors.IECNumber ? "input_error" : ""}
                        error={errors.IECNumber}
                      />
                    </Col>
                  </Row>
                </div>
              </div>
            </div>
          </Panel>
          {/* Others */}
          <Panel
            header="Others (All fields marked with * are mandatory.)"
            key="4"
          >
            <div className="card my-3">
              <div className="card-content">
                <div className="card-body">
                  <h6 className="fw-bold">Others</h6>
                  <Row gutter={[8, 8]}>
                    <Col xs={12} sm={12} md={12} lg={4}>
                      {form.country && form.country !== "106" ? (
                        // Show "Company Registration No" if the country is not India
                        <FormInput
                          disabled={disable}
                          name="companyRegistrationNo"
                          placeholder="Company Registration No *"
                          value={form.companyRegistrationNo}
                          onChange={handleInput}
                          maxLength={10}
                          error={errors.companyRegistrationNo}
                          className={
                            errors.companyRegistrationNo ? "input_error" : ""
                          }
                          required={form.country !== "106"}
                        />
                      ) : (
                        // Show "Permanent A/C No" if the country is India or no country is selected
                        <FormInput
                          disabled={disable}
                          name="pan"
                          placeholder="Permanent A/C No *"
                          value={form.pan}
                          onChange={handleInput}
                          maxLength={10}
                          error={errors.pan}
                          className={errors.pan ? "input_error" : ""}
                          required={form.country === "106"}
                        />
                      )}
                    </Col>

                    <Col xs={12} sm={12} md={12} lg={4}>
                      <FormInput
                        disabled={disable}
                        name="panRefNo"
                        placeholder="PAN Reference No"
                        value={form.panRefNo}
                        onChange={handleInput}
                        maxLength={10}
                      />
                    </Col>
                    <Col xs={12} sm={12} md={12} lg={4}>
                      <DatePicker
                        className="w-100"
                        placeholder="PAN Valid Date"
                        value={
                          form.panValidDate
                            ? moment(form.panValidDate, "YYYY-MM-DD")
                            : null
                        }
                        onChange={handleDate("panValidDate")}
                        disabled={disable}
                        format="DD-MM-YYYY"
                      />
                    </Col>
                    <Col xs={12} sm={12} md={12} lg={4}>
                      <FormInput
                        disabled={disable}
                        name="gstNo"
                        placeholder="GST No."
                        value={form.gstNo}
                        onChange={handleInput}
                        maxLength={15}
                      />
                    </Col>
                    <Col xs={12} sm={12} md={12} lg={4}>
                      <FormInput
                        disabled={disable}
                        name="eccNumber"
                        placeholder="ECC Number"
                        value={form.eccNumber}
                        onChange={handleInput}
                        maxLength={40}
                      />
                    </Col>

                    <Col xs={12} sm={12} md={12} lg={4}>
                      <FormInput
                        disabled={disable}
                        name="supplierMaster"
                        placeholder="Supplier Master"
                        value={form.supplierMaster}
                        onChange={handleInput}
                        maxLength={40}
                      />
                    </Col>

                    <Col xs={12} sm={12} md={12} lg={4}>
                      <Checkbox
                        disabled={disable}
                        checked={form.customerVendor}
                        onChange={handleCheckbox("customerVendor")}
                        className="mt-2"
                      >
                        Customs Vendor
                      </Checkbox>
                    </Col>
                    <Col xs={12} sm={12} md={12} lg={4}>
                      <FormInput
                        disabled={disable}
                        name="cstNo"
                        placeholder="Central Sales Tax No"
                        value={form.cstNo}
                        onChange={handleInput}
                        maxLength={40}
                      />
                    </Col>
                    <Col xs={12} sm={12} md={12} lg={4}>
                      <FormInput
                        disabled={disable}
                        name="lstNo"
                        placeholder="Local Sales Tax No"
                        value={form.lstNo}
                        onChange={handleInput}
                        maxLength={40}
                      />
                    </Col>
                    <Col xs={12} sm={12} md={12} lg={4}>
                      <FormInput
                        disabled={disable}
                        name="serviceTaxRegNo"
                        placeholder="Service Tax Reg. No"
                        value={form.serviceTaxRegNo}
                        onChange={handleInput}
                        maxLength={40}
                      />
                    </Col>
                  </Row>
                </div>
              </div>
            </div>
          </Panel>
          {/* Documents */}
          <Panel
            header="Documents (All fields marked with * are mandatory.)"
            key="5"
          >
            <div className="card my-3">
              <div className="card-content">
                <div className="card-body">
                  <Row gutter={[8, 8]} align="top">
                    <Col xs={12} sm={12} md={12} lg={4}>
                      <Upload
                        name="panCard"
                        multiple={false}
                        maxCount={1}
                        accept={ACCEPT_EXTS}
                        fileList={fileLists.panCard}
                        beforeUpload={beforeUploadFor("panCard")}
                        onRemove={onRemoveFor("panCard")}
                        onChange={onChangeFor("panCard")}
                        onPreview={handlePreview}
                        //  showUploadList={false}
                        customRequest={({ onSuccess }) => onSuccess("ok")}
                        showUploadList={false}
                      >
                        <Button
                          icon={<UploadOutlined />}
                          className={`w-100 d-flex align-items-center ${
                            errors.panCard ? "input_error" : ""
                          }`}
                          disabled={
                            isEditMode && canFullEdit
                              ? false
                              : isViewMode ||
                                isFinanceHodReadOnly ||
                                (isEditMode &&
                                  !userDetails.isVendor &&
                                  role !== "internal user")
                          }
                        >
                          {" "}
                          <span className=" truncate-text">
                            {" "}
                            {form.country === "106"
                              ? "PAN Card *"
                              : "Company Registration Certificate"}
                          </span>
                        </Button>
                      </Upload>
                      {fileLists.panCard?.length > 0 &&
                        fileLists.panCard[0] &&
                        (() => {
                          const file = fileLists.panCard[0];
                          const previewUrl = file.url || file.preview;

                          return (
                            <div
                              className="custom-file-list mt-2"
                              style={{ display: "flex", alignItems: "center" }}
                            >
                              <span title={file.name}>
                                {truncateName(file.name)}
                              </span>
                              {previewUrl && (
                                <span
                                  style={{
                                    color: "#000",
                                    transition: "color 0.2s",
                                  }}
                                  onMouseEnter={(e) =>
                                    (e.currentTarget.style.color = "blue")
                                  }
                                  onMouseLeave={(e) =>
                                    (e.currentTarget.style.color = "#000")
                                  }
                                >
                                  <EyeOutlined
                                    style={{
                                      fontSize: 14,
                                      cursor: "pointer",
                                      marginRight: 8,
                                    }}
                                    onClick={() =>
                                      handlePreview({
                                        ...file,
                                        url: previewUrl,
                                      })
                                    }
                                  />
                                </span>
                              )}
                              {previewUrl && (
                                <span
                                  style={{
                                    color: "#000",
                                    transition: "color 0.2s",
                                  }}
                                  onMouseEnter={(e) =>
                                    (e.currentTarget.style.color = "blue")
                                  }
                                  onMouseLeave={(e) =>
                                    (e.currentTarget.style.color = "#000")
                                  }
                                >
                                  <DownloadOutlined
                                    style={{ fontSize: 14, cursor: "pointer" }}
                                    onClick={() => handleDownload(previewUrl)}
                                  />
                                </span>
                              )}
                            </div>
                          );
                        })()}
                    </Col>

                    <Col xs={12} sm={12} md={12} lg={4}>
                      <Upload
                        name="gstCertificate"
                        multiple={false}
                        maxCount={1}
                        accept={ACCEPT_EXTS}
                        fileList={fileLists.gstCertificate}
                        beforeUpload={beforeUploadFor("gstCertificate")}
                        onRemove={onRemoveFor("gstCertificate")}
                        onChange={onChangeFor("gstCertificate")}
                        customRequest={({ onSuccess }) => onSuccess("ok")}
                        showUploadList={false}
                      >
                        <Button
                          icon={<UploadOutlined />}
                          disabled={
                            isEditMode && canFullEdit
                              ? false
                              : isViewMode ||
                                isFinanceHodReadOnly ||
                                (isEditMode &&
                                  !userDetails.isVendor &&
                                  role !== "internal user")
                          }
                          className="w-100 d-flex align-items-center"
                        >
                          <span className=" truncate-text">
                            GST Certificate
                          </span>
                        </Button>
                      </Upload>
                      {fileLists.gstCertificate?.length > 0 &&
                        fileLists.gstCertificate[0] &&
                        (() => {
                          const file = fileLists.gstCertificate[0];
                          const previewUrl = file.url || file.preview;
                          return (
                            <div
                              className="custom-file-list mt-2"
                              style={{ display: "flex", alignItems: "center" }}
                            >
                              <span title={file.name}>
                                {truncateName(file.name)}
                              </span>
                              {previewUrl && (
                                <span
                                  style={{
                                    color: "#000",
                                    transition: "color 0.2s",
                                  }}
                                  onMouseEnter={(e) =>
                                    (e.currentTarget.style.color = "blue")
                                  }
                                  onMouseLeave={(e) =>
                                    (e.currentTarget.style.color = "#000")
                                  }
                                >
                                  <EyeOutlined
                                    style={{
                                      fontSize: 14,
                                      cursor: "pointer",
                                      marginRight: 8,
                                    }}
                                    onClick={() =>
                                      handlePreview({
                                        ...file,
                                        url: previewUrl,
                                      })
                                    }
                                  />
                                </span>
                              )}
                              {previewUrl && (
                                <span
                                  style={{
                                    color: "#000",
                                    transition: "color 0.2s",
                                  }}
                                  onMouseEnter={(e) =>
                                    (e.currentTarget.style.color = "blue")
                                  }
                                  onMouseLeave={(e) =>
                                    (e.currentTarget.style.color = "#000")
                                  }
                                >
                                  <DownloadOutlined
                                    style={{ fontSize: 14, cursor: "pointer" }}
                                    onClick={() => handleDownload(previewUrl)}
                                  />
                                </span>
                              )}
                            </div>
                          );
                        })()}
                    </Col>

                    <Col xs={12} sm={12} md={12} lg={4}>
                      <Upload
                        name="cancelledCheque"
                        multiple={false}
                        maxCount={1}
                        accept={ACCEPT_EXTS}
                        fileList={fileLists.cancelledCheque}
                        beforeUpload={beforeUploadFor("cancelledCheque")}
                        onRemove={onRemoveFor("cancelledCheque")}
                        onChange={onChangeFor("cancelledCheque")}
                        customRequest={({ onSuccess }) => onSuccess("ok")}
                        showUploadList={false}
                      >
                        <Button
                          icon={<UploadOutlined />}
                          disabled={
                            isEditMode && canFullEdit
                              ? false
                              : isViewMode ||
                                isFinanceHodReadOnly ||
                                (isEditMode &&
                                  !userDetails.isVendor &&
                                  role !== "internal user")
                          }
                          className="w-100 d-flex align-items-center"
                        >
                          <span className=" truncate-text">
                            Cancelled Cheque
                          </span>
                        </Button>
                      </Upload>
                      {fileLists.cancelledCheque?.length > 0 &&
                        fileLists.cancelledCheque[0] &&
                        (() => {
                          const file = fileLists.cancelledCheque[0];
                          const previewUrl = file.url || file.preview;

                          return (
                            <div
                              className="custom-file-list mt-2"
                              style={{ display: "flex", alignItems: "center" }}
                            >
                              <span title={file.name}>
                                {truncateName(file.name)}
                              </span>
                              {previewUrl && (
                                <span
                                  style={{
                                    color: "#000",
                                    transition: "color 0.2s",
                                  }}
                                  onMouseEnter={(e) =>
                                    (e.currentTarget.style.color = "blue")
                                  }
                                  onMouseLeave={(e) =>
                                    (e.currentTarget.style.color = "#000")
                                  }
                                >
                                  <EyeOutlined
                                    style={{
                                      fontSize: 14,
                                      cursor: "pointer",
                                      marginRight: 8,
                                    }}
                                    onClick={() =>
                                      handlePreview({
                                        ...file,
                                        url: previewUrl,
                                      })
                                    }
                                  />
                                </span>
                              )}

                              {previewUrl && (
                                <span
                                  style={{
                                    color: "#000",
                                    transition: "color 0.2s",
                                  }}
                                  onMouseEnter={(e) =>
                                    (e.currentTarget.style.color = "blue")
                                  }
                                  onMouseLeave={(e) =>
                                    (e.currentTarget.style.color = "#000")
                                  }
                                >
                                  <DownloadOutlined
                                    style={{ fontSize: 14, cursor: "pointer" }}
                                    onClick={() => handleDownload(previewUrl)}
                                  />
                                </span>
                              )}
                            </div>
                          );
                        })()}
                    </Col>

                    <Col
                      xs={24}
                      sm={24}
                      md={12}
                      className="d-flex align-items-top justify-content-end"
                      style={{ color: "#888" }}
                    >
                      File type support PDF, PNG, JPEG, or JPG. (Max size: 15MB)
                    </Col>
                  </Row>

                  <hr className="my-3" />
                  <div>
                    {" "}
                    <h6 className="fw-bold">MSME Certificate</h6>
                  </div>

                  <Row gutter={[8, 8]}>
                    <Col xs={12} sm={12} md={12} lg={4}>
                      <FormInput
                        disabled={disable}
                        name="msmeNo"
                        placeholder={
                          form.vendorCategory !== "N/A"
                            ? "MSME No *"
                            : "MSME No"
                        }
                        value={form.msmeNo}
                        onChange={handleInput}
                        className={`w-100 ${
                          errors.msmeNo ? "input_error" : ""
                        }`}
                        error={errors.msmeNo}
                      />
                    </Col>
                    <Col xs={12} sm={12} md={12} lg={4}>
                      <FormInput
                        disabled={disable}
                        name="msmeCategory"
                        placeholder={
                          form.vendorCategory !== "N/A"
                            ? "MSME Category *"
                            : "MSME Category"
                        }
                        value={form.msmeCategory}
                        onChange={handleInput}
                        className={`w-100 ${
                          errors.msmeCategory ? "input_error" : ""
                        }`}
                        error={errors.msmeCategory}
                      />
                    </Col>
                    <Col xs={12} sm={12} md={12} lg={4}>
                      <DatePicker
                        placeholder={
                          form.vendorCategory !== "N/A"
                            ? "MSME Certificate Date *"
                            : "MSME Certificate Date"
                        }
                        value={
                          form.msmeCertDate
                            ? moment(form.msmeCertDate, "YYYY-MM-DD")
                            : null
                        }
                        onChange={handleDate("msmeCertDate")}
                        disabled={disable}
                        format="DD-MM-YYYY"
                        required={form.vendorCategory !== "N/A "}
                        className={`w-100 ${
                          errors.msmeCertDate ? "input_error" : ""
                        }`}
                        error={errors.msmeCertDate}
                      />
                      {errors.msmeCertDate && (
                        <p style={{ color: "red", fontSize: "12px" }}>
                          {errors.msmeCertDate}
                        </p>
                      )}
                    </Col>
                  </Row>

                  {/* <Row gutter={[8, 8]} className="mt-3">
                      <Col span={8}>
                        <FormInput
                        disabled={isViewMode || isFinanceHodReadOnly || (isEditMode && !userDetails.isVendor)}
                          name="companyRegistrationDetails"
                          placeholder="Company Registration Details"
                          value={form.companyRegistrationDetails}
                          onChange={handleInput}
                        />
                      </Col>
                    </Row> */}
                </div>
              </div>
            </div>
            <Button
              type="primary"
              shape="circle"
              size="large"
              onClick={scrollToTop}
              style={{
                position: "absolute",
                bottom: "75px",
                right: "20px",
                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.3)",
                backgroundColor: "#3CAFDA",
                border: "none",
                zIndex: 9999,
              }}
            >
              <UpOutlined
                style={{
                  display: "block",
                  alignItems: "center",
                }}
              />
            </Button>
          </Panel>
          {showDataInfoPanel && (
            <Panel
              header="Data Information (All fields marked with * are mandatory.)"
              key="6"
            >
              {/* Purchase Data — ONLY for internal user & admin */}
              {showPurchaseData && (
                <div className="card my-3">
                  <div className="card-content">
                    <div className="card-body">
                      <h6 className="fw-bold mb-2">Purchase Data</h6>
                      <Row gutter={[12, 12]}>
                        <Col xs={12} sm={12} md={12} lg={4}>
                          <FormSelect
                            name="purchaseOrg"
                            placeholder="Purchase Org.*"
                            value={form.purchaseOrg}
                            onChange={handleSelect("purchaseOrg")}
                            options={purchaseOrgOptions}
                            showSearch
                            disabled={!canEditPurchaseData}
                            className={`w-100 ${
                              errors.purchaseOrg ? "input_error" : ""
                            }`}
                            error={errors.purchaseOrg}
                          />
                        </Col>
                        <Col xs={12} sm={12} md={12} lg={4}>
                          <FormSelect
                            name="orderCurrency"
                            placeholder="Order Currency*"
                            value={form.orderCurrency}
                            onChange={handleSelect("orderCurrency")}
                            options={orderCurrencyOptions}
                            showSearch
                            disabled={!canEditPurchaseData}
                            className={`w-100 ${
                              errors.orderCurrency ? "input_error" : ""
                            }`}
                            error={errors.orderCurrency}
                          />
                        </Col>
                        <Col xs={12} sm={12} md={12} lg={4}>
                          <FormSelect
                            name="paymentTerm"
                            placeholder="Payment Term*"
                            value={form.paymentTerm}
                            onChange={handleSelect("paymentTerm")}
                            options={paymentTermOptions}
                            showSearch
                            disabled={!canEditPurchaseData}
                            className={`w-100 ${
                              errors.paymentTerm ? "input_error" : ""
                            }`}
                            error={errors.paymentTerm}
                          />
                        </Col>
                        <Col xs={12} sm={12} md={12} lg={4}>
                          <FormSelect
                            name="purchaseGroup"
                            placeholder="Purchase Group*"
                            value={form.purchaseGroup}
                            onChange={handleSelect("purchaseGroup")}
                            options={purchaseGroupOptions}
                            showSearch
                            disabled={!canEditPurchaseData}
                            className={`w-100 ${
                              errors.purchaseGroup ? "input_error" : ""
                            }`}
                            error={errors.purchaseGroup}
                          />
                        </Col>
                        <Col xs={12} sm={12} md={12} lg={4}>
                          <FormInput
                            name="plannedDeliveryDelay"
                            placeholder="Planned Del. Delays"
                            value={form.plannedDeliveryDelay}
                            onChange={handleInput}
                            disabled={!canEditPurchaseData}
                            maxLength={3}
                          />
                        </Col>
                        <Col xs={12} sm={12} md={12} lg={4}>
                          <FormSelect
                            name="schemaGroup"
                            placeholder="Schema Group"
                            value={form.schemaGroup}
                            onChange={handleSelect("schemaGroup")}
                            options={schemaGroupOptions}
                            showSearch
                            disabled={!canEditPurchaseData}
                          />
                        </Col>
                        <Col xs={12} sm={12} md={12} lg={4}>
                          <FormInput
                            name="incoterm"
                            placeholder="Incoterms"
                            value={form.incoterm}
                            onChange={handleInput}
                            disabled={!canEditPurchaseData}
                            maxLength={3}
                          />
                        </Col>
                        <Col xs={12} sm={12} md={12} lg={4}>
                          <FormInput
                            name="incotermLocation"
                            placeholder="Incoterms Location"
                            value={form.incotermLocation}
                            onChange={handleInput}
                            disabled={!canEditPurchaseData}
                            maxLength={70}
                          />
                        </Col>
                      </Row>

                      <Row className="mt-3">
                        <Col span={12}>
                          <Checkbox
                            disabled={!canEditPurchaseData}
                            checked={form.grInvoiceVerification}
                            onChange={handleCheckbox("grInvoiceVerification")}
                          >
                            GR-based invoice verification
                          </Checkbox>
                        </Col>
                        <Col span={12}>
                          <Checkbox
                            disabled={!canEditPurchaseData}
                            checked={form.serviceInvoiceVerification}
                            onChange={handleCheckbox(
                              "serviceInvoiceVerification"
                            )}
                          >
                            Service based Invoice Verification
                          </Checkbox>
                        </Col>
                      </Row>
                    </div>
                  </div>
                </div>
              )}

              {/* Company Data — ONLY for finance team & admin */}
              {showCompanyData && (
                <div className="card my-3">
                  <div className="card-content">
                    <div className="card-body">
                      <h6 className="fw-bold mb-2">Company Data</h6>
                      <Row gutter={[12, 12]}>
                        <Col xs={12} sm={12} md={12} lg={4}>
                          <FormInput
                            name="companyCode"
                            placeholder="Company Code *"
                            value={`${localStorage.getItem("Company_Name")} - ${
                              form.companyCode || ""
                            }`}
                            onChange={(e) =>
                              setForm((prev) => ({
                                ...prev,
                                companyCode: e.target.value,
                              }))
                            }
                            disabled={!canEditCompanyData}
                            error={errors.companyCode}
                            maxLength={4}
                            readOnly={true}
                          />
                        </Col>
                        <Col xs={12} sm={12} md={12} lg={4}>
                          <FormSelect
                            name="reconciliationAccount"
                            placeholder="Reconciliation A/C *"
                            value={form.reconciliationAccount}
                            onChange={handleSelect("reconciliationAccount")}
                            options={reconAccountOptions}
                            showSearch
                            disabled={!canEditCompanyData}
                            error={errors.reconciliationAccount}
                          />
                        </Col>
                        <Col xs={12} sm={12} md={12} lg={4}>
                          <FormInput
                            name="headOfficeAccountNo"
                            placeholder="HO Account No"
                            value={form.headOfficeAccountNo}
                            onChange={handleInput}
                            disabled={!canEditCompanyData}
                            error={errors.headOfficeAccountNo}
                          />
                        </Col>
                        <Col xs={12} sm={12} md={12} lg={4}>
                          <FormSelect
                            name="companyPaymentTerm"
                            placeholder="Company Payment Term *"
                            value={form.companyPaymentTerm}
                            onChange={handleSelect("companyPaymentTerm")}
                            options={paymentTermOptions}
                            showSearch
                            disabled={!canEditCompanyData}
                            error={errors.companyPaymentTerm}
                          />
                        </Col>
                        <Col xs={12} sm={12} md={12} lg={4}>
                          <FormSelect
                            name="paymentMethod"
                            placeholder="Payment Method *"
                            value={form.paymentMethod}
                            onChange={handleSelect("paymentMethod")}
                            options={paymentMethodOptions}
                            showSearch
                            disabled={!canEditCompanyData}
                            error={errors.paymentMethod}
                          />
                        </Col>
                        <Col xs={12} sm={12} md={12} lg={4}>
                          <FormSelect
                            name="houseBank"
                            placeholder="House Bank"
                            value={form.houseBank}
                            onChange={handleSelect("houseBank")}
                            options={houseBankOptions}
                            showSearch
                            disabled={!canEditCompanyData}
                          />
                        </Col>
                        <Col xs={12} sm={12} md={12} lg={4}>
                          <FormInput
                            name="vendorAccountGroup"
                            placeholder="Vendor Account Group"
                            value={form.vendorAccountGroup}
                            onChange={handleInput}
                            disabled={!canEditCompanyData}
                            maxLength={4}
                          />
                        </Col>
                        <Col xs={12} sm={12} md={12} lg={4}>
                          <FormSelect
                            name="withholdingTaxType"
                            placeholder="Withholding Tax Type 1"
                            value={form.withholdingTaxType}
                            onChange={handleSelect("withholdingTaxType")}
                            options={withholdingTypeOptions}
                            showSearch
                            disabled={!canEditCompanyData}
                          />
                        </Col>
                        <Col xs={12} sm={12} md={12} lg={4}>
                          <FormSelect
                            name="withholdingTaxCode"
                            placeholder="Withholding Tax Code 1"
                            value={form.withholdingTaxCode}
                            onChange={handleSelect("withholdingTaxCode")}
                            options={withholdingCodeOptions1}
                            showSearch
                            disabled={!canEditCompanyData}
                          />
                        </Col>
                        <Col xs={12} sm={12} md={12} lg={4}>
                          <Checkbox
                            className="mt-1"
                            disabled={!canEditCompanyData}
                            checked={form.withholdingSubject}
                            onChange={handleCheckbox("withholdingSubject")}
                          >
                            Subject to withholding
                          </Checkbox>
                        </Col>
                      </Row>

                      <Row className="mt-3">
                        <Col span={12}>
                          <Checkbox
                            disabled={!canEditCompanyData}
                            checked={form.doubleInvoiceCheck}
                            onChange={handleCheckbox("doubleInvoiceCheck")}
                          >
                            Check double invoice
                          </Checkbox>
                        </Col>
                      </Row>

                      {/* Exemption Section */}
                      {showExemptionSection && (
                        <Row className="mt-3" gutter={[12, 12]}>
                          <Col xs={12} sm={12} md={12} lg={4}>
                            <FormInput
                              name="sectionCode"
                              placeholder="Section Code"
                              value={form.sectionCode}
                              onChange={handleInput}
                              disabled={!canEditCompanyData}
                              maxLength={4}
                            />
                          </Col>
                          <Col xs={12} sm={12} md={12} lg={4}>
                            <FormInput
                              name="exemptionNumber"
                              placeholder="Exemption Number"
                              value={form.exemptionNumber}
                              onChange={handleInput}
                              disabled={!canEditCompanyData}
                              maxLength={25}
                            />
                          </Col>
                          <Col xs={12} sm={12} md={12} lg={4}>
                            <FormInput
                              name="exemptionRate"
                              placeholder="Exemption Rate"
                              value={form.exemptionRate}
                              onChange={handleInput}
                              disabled={!canEditCompanyData}
                              maxLength={5}
                            />
                          </Col>
                          <Col xs={12} sm={12} md={12} lg={4}>
                            <DatePicker
                              className="w-100"
                              placeholder="Exemption To"
                              value={
                                form.exemptionTo
                                  ? moment(form.exemptionTo, "YYYY-MM-DD")
                                  : null
                              }
                              onChange={handleDate("exemptionTo")}
                              disabled={!canEditCompanyData}
                              maxLength={8}
                              format="DD-MM-YYYY"
                            />
                          </Col>
                          <Col xs={12} sm={12} md={12} lg={4}>
                            <FormInput
                              name="exemptionReason"
                              placeholder="Exemption Reason"
                              value={form.exemptionReason}
                              onChange={handleInput}
                              disabled={!canEditCompanyData}
                              maxLength={2}
                            />
                          </Col>
                          <Col xs={12} sm={12} md={12} lg={4}>
                            <FormSelect
                              name="withholdingTax"
                              placeholder="Withholding Tax"
                              value={form.withholdingTax}
                              onChange={handleSelect("withholdingTax")}
                              options={withholdingTypeOptions}
                              showSearch
                              disabled={!canEditCompanyData}
                            />
                          </Col>

                          <Col xs={12} sm={12} md={12} lg={4}>
                            <FormSelect
                              name="WithholdingTaxCode"
                              placeholder="Withholding Tax Code"
                              value={form.WithholdingTaxCode}
                              onChange={handleSelect("WithholdingTaxCode")}
                              options={withholdingCodeOptions2}
                              showSearch
                              disabled={!canEditCompanyData}
                            />
                          </Col>

                          <Col xs={12} sm={12} md={12} lg={4}>
                            <FormInput
                              name="thresholdAmount"
                              placeholder="Threshold Amount"
                              value={form.thresholdAmount}
                              onChange={handleInput}
                              disabled={!canEditCompanyData}
                              maxLength={15}
                            />
                          </Col>
                          <Col xs={12} sm={12} md={12} lg={4}>
                            <FormInput
                              name="currencyType"
                              placeholder="Currency Type"
                              value={form.currencyType}
                              onChange={handleInput}
                              disabled={!canEditCompanyData}
                              maxLength={5}
                            />
                          </Col>
                        </Row>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Comment block (optional — keep if you want it visible whenever Data Information shows) */}
              {userDetails?.isEmployee && !isView && !isAdmin && (
                <div className="card my-3">
                  <div className="card-content">
                    <div className="card-body">
                      <h6 className="fw-bold mb-2">Comment</h6>
                      <TextArea
                        name="dataInfoComment"
                        className={`form-control ${
                          errors.dataInfoComment ? "input_error" : ""
                        }`}
                        placeholder="Write a Comment"
                        value={form.dataInfoComment}
                        onChange={handleInput}
                        rows={3}
                        error
                      />{" "}
                      <p className="inputError">{errors.dataInfoComment}</p>
                    </div>
                  </div>
                </div>
              )}
            </Panel>
          )}
        </Collapse>

        <div className="pt-0 reset_save_btn justify-content-between mb-2">
          {!isView && (
            <>
              {canFullEdit || !isFinanceHodReadOnly ? (
                <div>
                  <Button
                    className="save_btn"
                    loading={loading}
                    onClick={() => {
                      saveVendorData();
                    }}
                    style={{ marginLeft: "0px", marginRight: "10px" }}
                    disabled={isView}
                  >
                    Save
                  </Button>
                  <Button
                    className="reset_btn"
                    onClick={() => {
                      if (initialForm) {
                        const cleared = Object.keys(form).reduce((acc, key) => {
                          if (
                            initialForm[key] !== "" &&
                            initialForm[key] !== null &&
                            initialForm[key] !== undefined
                          ) {
                            acc[key] = initialForm[key];
                          } else {
                            acc[key] = "";
                          }
                          return acc;
                        }, {});

                        setForm(cleared);
                        setErrors({});
                      } else {
                        setForm({
                          vendorType: "",
                          country: null,
                          language: null,
                          regionCode: null,
                          vendorCategory: null,
                          titleKey: "",
                          name1: "",
                          name2: "",
                          name3: "",
                          name4: "",
                          coName: "",
                          street1: "",
                          street2: "",
                          street3: "",
                          street4: "",
                          location: "",
                          district: "",
                          postalCity: "",
                          cityName: "",
                          transportZone: "",
                          certificateDate: null,
                          telephone: "",
                          fax: "",
                          mobile1: "",
                          mobile2: "",
                          mobile3: "",
                          email1: "",
                          email2: "",
                          bankDetailsId: "0001",
                          bankCountryKey: "",
                          bankKey: "",
                          bankAccountNo: "",
                          bankName: "",
                          IFSCOrSWIFTCode: "",
                          IECNumber: "",
                          pan: "",
                          panRefNo: "",
                          panValidDate: null,
                          gstNo: "",
                          eccNumber: "",
                          supplierMaster: "",
                          customerVendor: false,
                          cstNo: "",
                          lstNo: "",
                          serviceTaxRegNo: "",
                          msmeNo: "",
                          msmeCategory: "",
                          msmeCertDate: null,
                          purchaseOrg: "",
                          orderCurrency: "",
                          paymentTerm: "",
                          purchaseGroup: "",
                          plannedDeliveryDelay: "",
                          schemaGroup: "",
                          incoterm: "",
                          incotermLocation: "",
                          grInvoiceVerification: true,
                          serviceInvoiceVerification: true,
                          companyCode: localStorage.getItem("Company_Code"),
                          reconciliationAccount: "",
                          headOfficeAccountNo: "",
                          companyPaymentTerm: "",
                          paymentMethod: "",
                          houseBank: "",
                          vendorAccountGroup: "",
                          withholdingTaxType: "",
                          withholdingTaxCode: "",
                          withholdingSubject: false,
                          doubleInvoiceCheck: true,
                          sectionCode: "",
                          exemptionNumber: "",
                          exemptionRate: "",
                          exemptionTo: null,
                          exemptionReason: "",
                          withholdingTax: "",
                          WithholdingTaxCode: "",
                          thresholdAmount: "",
                          currencyType: "",
                          dataInfoComment: "",
                        });

                        setErrors({});
                        setFiles({
                          panCard: null,
                          gstCertificate: null,
                          cancelledCheque: null,
                        });
                        setFileLists({
                          panCard: [],
                          gstCertificate: [],
                          cancelledCheque: [],
                        });
                      }
                    }}
                    disabled={isView}
                    style={{ marginLeft: "0px" }}
                  >
                    Reset
                  </Button>
                </div>
              ) : (
                <div></div>
              )}

              {userDetails?.isEmployee && !isAdmin && (
                <div>
                  <Button
                    className="save_btn"
                    onClick={() => handleVendorAction("Approve")}
                    disabled={isView || actionLoading !== null}
                    loading={actionLoading === "Approve"}
                    style={{ marginLeft: "0px", marginRight: "10px" }}
                  >
                    Approve
                  </Button>

                  <Button
                    className="reset_btn"
                    style={{ marginRight: "10px" }}
                    onClick={() => handleVendorAction("Reject")}
                    disabled={isView || actionLoading !== null}
                    loading={actionLoading === "Reject"}
                  >
                    Reject
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewVendorDetail;
